import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../lib/api";
import { Input } from "./ui/input";
import { MapPin, History, Plus, Globe2, LocateFixed } from "lucide-react";
import GroundSearchDialog from "./GroundSearchDialog";
import { placeTypeLabel } from "../lib/placeType";
import { resolveStoredGround } from "../lib/groundRef";
import { isArchived } from "../lib/archived";
import { groupGroundSuggestions } from "../lib/groundSuggestions";
import {
  DIRECTORY_UNAVAILABLE_MESSAGE,
  NEAR_ME_BUSY_LABEL,
  NEAR_ME_LABEL,
  NEAR_ME_RADIUS_KM,
  findNearMe,
} from "../lib/nearMe";
// Issue #690 — the general (non-saved) suggestion list is now the server
// SEARCH (#644's `?q=`), not a mount-time fetch of the whole directory. See
// the module doc below for why, and `useDirectorySearch`'s own header for
// the debounce/race machinery this reuses rather than re-implements.
import { useDirectorySearch } from "../hooks/useDirectorySearch";
import { readViewportGrounds } from "../lib/mapViewport";
import { isProgrammaticFocus } from "../lib/focusFirstInvalid";

/**
 * Issue #136 — the type on a dropdown row, small enough not to compete with
 * the name and absent entirely when there is nothing to say. A row with no
 * type is a free-text venue from match history that matches no directory
 * row; it gets no tag rather than a guessed "Field".
 */
function PlaceTypeTag({ value, testId }) {
  const label = placeTypeLabel(value);
  if (!label) return null;
  return (
    <span
      className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600"
      data-testid={testId}
    >
      {label}
    </span>
  );
}

/** Nothing asked for, nothing known — the state the picker opens in and returns to. */
const NEAR_ME_IDLE = { status: "idle", results: [], message: "", position: null };

/**
 * One suggestion row — issue #689: the saved group and the general list are
 * two different sources feeding the SAME row, not a new pattern invented for
 * either. Pin icon for a saved row, history icon otherwise, exactly as the
 * general list has always rendered it.
 */
function GroundOption({ ground: s, onSelect, testId }) {
  return (
    <button
      type="button"
      className="w-full text-left px-3 min-h-11 py-2 flex items-center justify-between gap-2 hover:bg-slate-50 text-sm"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onSelect}
      data-testid={testId}
    >
      <span className="flex items-center gap-1.5 min-w-0">
        {s.source === "saved"
          ? <MapPin size={12} className="shrink-0 text-[#0B2E59]" />
          : <History size={12} className="shrink-0 text-slate-400" />}
        <span className="truncate">{s.name}</span>
        <PlaceTypeTag value={s.placeType} testId={`${testId}-type`} />
      </span>
      {(s.city || s.country) && (
        <span className="text-[11px] text-slate-400 truncate shrink-0">
          {[s.city, s.country].filter(Boolean).join(", ")}
        </span>
      )}
    </button>
  );
}

/**
 * Ground picker (issue #59 / B3, mirrored for #128) — a PICKER, and since
 * issue #356 that word is load-bearing rather than descriptive.
 *
 * WHAT IT PRODUCES. A pair: `value` (the name shown) and `groundId` (the
 * record behind it), handed back together through `onChange(name, groundId)`.
 * A field holding a name with no id is UNRESOLVED, and the API will not
 * store it — `matches.ground_id` and `training_sessions.ground_id` are real
 * foreign keys now, and the free-text column beside each is a label derived
 * from the record. So this component's job stopped being "suggest a
 * spelling" and became "return a ground".
 *
 * Suggestions come from THREE sources, merged and deduped by ground id
 * (falling back to the lowercased name for rows that have no id):
 *   - `GET /grounds?q=<value>` — THE SHARED DIRECTORY, reached by SEARCH
 *     rather than by a whole-directory fetch (issue #690). Before #690 this
 *     was `GET /grounds` on mount, unfiltered and unbounded — the exact
 *     defect #690 was filed over: the world import landed and this fetch
 *     started downloading the entire visible directory before the player
 *     typed a character. It is now the SAME live search the map's card list
 *     uses (`useDirectorySearch`, #644), driven by this component's own
 *     `value` — which also means the zero-tap legacy-name convergence below
 *     gets its candidate for free, as one more search rather than a special
 *     case. Required by enforcement rather than merely nice: if the only
 *     suggestions were the caller's own bookmarks and history, a player
 *     could never pick a ground they had not already used, and there would
 *     be nothing left to do but type — which is the thing that no longer
 *     saves. `?q=` reaches the SAME visibility scope the old mount fetch
 *     did (`listVisibleGrounds`'s predicate — global/verified plus the
 *     caller's own), so nothing that was reachable before is unreachable
 *     now; the difference is that reaching it needs a keystroke.
 *   - `GET /players/me/grounds` — the player's saved/bookmarked grounds.
 *     Issue #689: these are pulled OUT of the merged, alphabetical list and
 *     rendered as their own "Saved grounds" group at the head of the panel
 *     (`groupGroundSuggestions`, `lib/groundSuggestions.js`) — mirroring the
 *     "Nearest to you" group below (issue #134). Sorting them into the
 *     alphabetical merge, which is what this list still does for every
 *     OTHER purpose, is exactly the defect #689 fixed: once the shared
 *     directory held the world, a player's own bookmarks were wherever the
 *     alphabet put them and the 8-row cut rarely reached that far.
 *   - `GET /venues/played` — venues from the player's own match history,
 *     but ONLY the rows that resolve to a directory record (`ground_id`).
 *     A history row that matches nothing is a legacy free-text name; it can
 *     no longer be saved, so offering it would be offering a dead end. It
 *     is dropped rather than shown-and-then-refused.
 *
 * Where sources overlap, the SAVED/DIRECTORY spelling wins over the history
 * entry — the record is canonical, the history entry is whatever got typed
 * that day. `snapToCanonical` (fired on blur) resolves a typed name against
 * the same merged list, so typing a case/whitespace variant of something
 * known now yields the RECORD, not merely the canonical spelling ("Eden
 * Park" and "thornmere park " no longer become two venues, and neither becomes a
 * string).
 *
 * ZERO-TAP CONVERGENCE FOR LEGACY ROWS (#356, product decision 2). Editing
 * a match logged before ground records existed requires re-picking the
 * ground. Almost always the stored name IS a ground in the directory, so
 * this component resolves it on the caller's behalf the moment the
 * suggestions load (`resolveStoredGround`, `lib/groundRef.js` — whole-name
 * match, refusing ambiguity). Since #690 that load IS the search above,
 * driven by this same stored `value` on mount — so a legacy name resolves
 * as soon as its own search answers, with no special case for "this value
 * arrived from a prop instead of a keystroke". The player fixing a score on
 * a 2024 match is asked nothing. Only a name the directory does not know
 * reaches the re-pick, and for that name the panel opens one tap from
 * `Add "{name}"`.
 *
 * When the typed value matches nothing, the dropdown leads with the
 * ACTION (issue #210). It used to lead with an apology — italic grey
 * "no saved grounds match", a hedge about free text, and the add option
 * buried at the bottom — so the app announced what it couldn't do and
 * hid the thing the player wanted. The capability was always here; the
 * presentation was backwards. Now: `Add "{typed}"` is the headline, and
 * the two paths that already live inside `GroundSearchDialog` are one tap
 * each beneath it — "Search worldwide" (OSM, seeded with what was typed,
 * so the venue arrives with real coordinates) and "Enter manually" (pin
 * and details when OSM doesn't know the place). That mirrors the Emergent
 * prototype's flow, which offered exactly those two as the visible
 * choices in one dialog (`docs/prototype-history.md`, 2026-07-25 and the
 * "Match Entry inline + Add new ground" note) — same screen, no second
 * surface, no third pattern.
 *
 * Free text no longer saves (#356), and NOBODY IS BLOCKED BY THAT, which
 * was the whole condition #210 attached to the correction: manual entry
 * inside `GroundSearchDialog` succeeds with no network round trip, so a
 * player at an unmapped ground with no signal still logs their innings —
 * they create a real record while doing it. What they cannot do any more is
 * mint a string that looks like a venue and joins to nothing.
 *
 * On save the dialog fills this field with the new ground's name AND —
 * via `POST /grounds`'s existing auto-bookmark behavior
 * (`routes/grounds.ts`) — lands it in the player's saved grounds, which
 * `handleSaved` re-reads immediately, so the ground added this way is
 * pinned, appears on the map, and is re-suggested without a reload.
 *
 * Per-caller scoping is unchanged and now covers three endpoints rather
 * than two: bookmarks and history are the caller's own, and `GET /grounds`
 * returns global/verified grounds plus the caller's own — never another
 * user's private pin. That is the same predicate the API enforces on the
 * write path (`resolveGroundForWrite`), so this list cannot offer a ground
 * the save would then refuse.
 *
 * Issue #134 adds "Use my location" to the top of the dropdown, so both
 * consumers of this component — the match form and the training form — get
 * it from one implementation. Pressing it reads the device position ONCE,
 * ranks the already-loaded grounds directory ON THE DEVICE (`lib/nearMe.js`,
 * over `lib/distance.js`'s haversine from #133) and lists the nearest with
 * their distances. Nothing carries the position off the device; denied,
 * unavailable and timed-out each get one plain line and the field keeps
 * working. See `lib/nearMe.js` for why that is a structural property of
 * these modules rather than a promise in a comment.
 *
 * Issue #136 puts the PLACE TYPE on every row this component offers — the
 * saved/history suggestions and the near-me results alike. The reason it
 * belongs here and not only on the map: this dropdown is where a player
 * standing somewhere unfamiliar picks the venue they are about to log, and
 * "Torvale Domain" and "Torvale Domain Nets" are two rows that otherwise
 * differ by one word they may not read. The type carries through from the
 * two source endpoints (`/players/me/grounds` and `/venues/played`), so a
 * history-only venue that matches no directory row shows NO type rather
 * than a guessed one.
 *
 * THE COACH PATH (#356, product decision 1). A coach has no player profile,
 * so `GET /players/me/grounds` and `GET /venues/played` answer 404/400 for
 * them — which, before #356, meant a coach scheduling training saw ZERO
 * suggestions, and enforcement would have left them with a field they could
 * not satisfy. The directory source is still the answer — it is open to
 * every authenticated role already, needs no new route and no new table,
 * and search reaches the grounds a coach created themselves (visible to
 * them and to nobody else) exactly as it reaches the shared ones. The two
 * per-player calls still fail for a coach and are still caught and treated
 * as "no suggestions from that source" rather than an error.
 *
 * #690's REGRESSION, NAMED RATHER THAN HIDDEN: before #690 the directory
 * arrived on mount, so a coach with no bookmarks and no history still saw
 * their own grounds listed with NOTHING typed — the zero-tap case #689 later
 * gave players their own version of, via bookmarks. #689's saved-grounds
 * group has no coach equivalent (`player_grounds` is keyed to
 * `player_profiles` — see below), and #690 removed the whole-directory
 * mount fetch that was filling the same role for a coach. So a coach who
 * has created grounds before now sees an EMPTY panel until they type — the
 * ground is still there, `?q=` still finds it (same visibility scope,
 * unchanged), but the zero-typing convenience #356 built is gone for this
 * one role. No replacement is built here: the honest fix is a coach-scoped
 * "my grounds" source, which is a new decision, not a search wiring change.
 *
 * What a coach still does NOT get is a per-coach BOOKMARK. `player_grounds`
 * is keyed to `player_profiles`, so a coach has no row to own; giving them
 * one means re-keying that table off `users`, which is a table REBUILD and
 * does not belong in the same migration as two additive foreign keys. The
 * gap is narrow — a coach's own created grounds are reachable by typing —
 * and it is named here rather than left to be discovered.
 */
export default function GroundPicker({
  value,
  /**
   * Issue #356 — the other half of the pair. `null` means the field holds a
   * name nobody has resolved yet (freshly typed, or a legacy row's stored
   * string before `resolveStoredGround` gets to it).
   */
  groundId = null,
  /** `(name, groundId)` — always both, never one. See the module doc. */
  onChange,
  placeholder = "e.g. Thornmere Park",
  className = "",
  inputTestId,
  testIdPrefix = "ground-picker",
  // Issue #152: a session that came from a booking takes its location from
  // the booking. The suggestion panel is suppressed along with the input —
  // a read-only field that still pops a picker on focus is worse than one
  // that does nothing.
  disabled = false,
  // Issue #1008 (D203.1, PR #1025 review F1) — forwarded verbatim to the
  // "Add new ground…" `GroundSearchDialog` below. False by default, so
  // this picker's un-flagged legacy callers (`PerformanceTracking.jsx:994`,
  // `TrainingSessions.jsx:573`, the old 31-input dialog and the training
  // session form) are byte-identical. `LogMatchScorecardStep.jsx` — mounted
  // only while `newUi("log-scorecard")` is true — passes it through from
  // `PerformanceTracking.jsx`'s own gate. See `GroundSearchDialog.jsx`'s
  // own header for why the flag is read at the gated call site rather than
  // inside either shared component.
  hideIndoor = false,
  // #1299 (D304) — optional ARIA for a caller that marks this field invalid
  // (the old match dialog). Undefined renders no attribute, so every other
  // caller is unchanged.
  ariaInvalid,
  ariaDescribedBy,
  // #1299 (D312) — false keeps the list shut when Save's focus move lands here
  // (`focusFirstInvalid`); a tap on the field opens it. A player's own tap or
  // Tab still opens it. True by default, so every other caller is unchanged.
  openOnProgrammaticFocus = true,
  // #1305 (D303) — an inline style for the input, for a caller that marks the
  // field invalid on the new look (the training session form's `--nw-border-invalid`
  // border). Undefined renders no `style` attribute, so every other caller is
  // unchanged.
  inputStyle,
  // #1307 (D303) — forwarded to the "Add new ground…" `GroundSearchDialog`: true
  // marks each missing manual field instead of toasting. False by default, so
  // every caller but the new-look ones is unchanged.
  markFieldErrors = false,
  // #1364 — false stops the zero-tap convergence effect from resolving text
  // the player is typing right now. "Thornmere" is a whole ground's name and
  // also the start of "Thornmere Park"; resolving it mid-keystroke counts as a
  // pick to a caller that closes on one, and the longer name can no longer be
  // chosen. With false, a typed name resolves on blur (`snapToCanonical`) or
  // on an explicit pick. A value that arrives from outside (a stored row, a
  // restored form) still converges silently. True by default, so every other
  // caller is unchanged.
  autoResolveWhileTyping = true,
  // #1373 (D382) — optional CSS margin-top for the suggestion list, so a
  // caller with a row under the input (the edit step's Cancel) can open the
  // list below it. Undefined renders no style attribute: every other caller
  // is byte-identical.
  dropdownOffset,
  // #1373 (D382a) — optional `(open) => void`, told whenever the list opens or
  // closes, for a caller that makes room for it. Absent: nothing is called.
  onOpenChange,
}) {
  const [savedGrounds, setSavedGrounds] = useState([]);
  const [venuesPlayed, setVenuesPlayed] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => { if (onOpenChange) onOpenChange(open && !disabled); }, [open, disabled]); // eslint-disable-line react-hooks/exhaustive-deps
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogSeed, setDialogSeed] = useState("");
  const [dialogTab, setDialogTab] = useState(null);
  const blurTimer = useRef(null);
  // #1364 — set once the player has typed in the field (see `handleTyped`).
  const typedByPlayer = useRef(false);

  // ---------- Issue #134: "Use my location" ----------
  //
  // `nearMe.status` is idle -> locating -> ready | error, and `position` is
  // held here and NOWHERE else: no storage, no ref that outlives the
  // component, no logging. It is used for exactly two things — ranking the
  // directory that was already downloaded (on-device, in `findNearMe`), and,
  // if the player presses again, seeding the add-a-ground dialog through
  // `addHerePin`. See `lib/nearMe.js` for the full argument.
  const [nearMe, setNearMe] = useState(NEAR_ME_IDLE);
  const [addHerePin, setAddHerePin] = useState(null);
  // Issue #690 — THE ONLY REMAINING `GET /grounds` (no `q`, no `bbox`) IN
  // THIS COMPONENT, and it is lazy: fetched on the FIRST near-me press,
  // never on mount, so a picker nobody asks about — and nobody presses
  // "Use my location" on — costs nothing. It still completes before any
  // position is read (`loadDirectory`, below, carries the reason),
  // preserving `lib/nearMe.js`'s structural privacy guarantee: the position
  // never decides what gets requested, only how an already-fetched list
  // gets RANKED. The request itself is now capped server-side
  // (`GROUNDS_BBOX_MAX_ROWS`, api/src/db/repos/grounds.ts) rather than
  // truly unbounded, same as every other caller of the bare route.
  const directoryRef = useRef(null);

  const loadSaved = () => api.get("/players/me/grounds").then((r) => setSavedGrounds(r.data || [])).catch(() => setSavedGrounds([]));
  const loadPlayed = () => api.get("/venues/played").then((r) => setVenuesPlayed(r.data || [])).catch(() => setVenuesPlayed([]));

  useEffect(() => {
    loadSaved();
    loadPlayed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Issue #690 — THE GENERAL SUGGESTION SOURCE, live rather than a mount-time
   * snapshot. `useDirectorySearch` is #644's own hook (`GroundsMap.jsx`'s
   * card list uses it too) — debounced, race-safe, and driven by this
   * component's own `value`, so a legacy stored name (the zero-tap
   * convergence effect below) is answered by the SAME request a keystroke
   * would produce, rather than a special case. `isAdmin` is always `false`
   * here — a picker never offers an archived venue, on either form it
   * appears in.
   */
  const search = useDirectorySearch(api, value, false);

  useEffect(() => () => { if (blurTimer.current) clearTimeout(blurTimer.current); }, []);

  /**
   * Merge + dedupe. Keyed by GROUND ID now (#356) rather than by name: two
   * directory rows may legitimately share a name — a nets facility and the
   * field beside it, #136 — and collapsing them by name would hide one of
   * them from a picker whose whole job is to return a specific record.
   *
   * Order is precedence, weakest first: history, then directory, then the
   * caller's own bookmarks. A ground the player has saved is described the
   * way they saved it and marked `saved` so it draws the pin icon.
   *
   * History rows with NO `ground_id` are dropped — see the module doc. This
   * component only ever offers something that can actually be saved.
   *
   * `search.rows` (issue #690) is this component's ONLY directory-sourced
   * general suggestion now — empty until `value` names a term the debounced
   * search has answered. See the module doc for why that is a deliberate
   * trade rather than an oversight.
   */
  const suggestions = useMemo(() => {
    const map = new Map();
    const put = (row) => {
      if (!row.name?.trim()) return;
      map.set(row.groundId || `name:${row.name.trim().toLowerCase()}`, row);
    };
    for (const v of venuesPlayed) {
      if (!v.ground_id) continue;
      // #166 FAC-11: history keeps the archived venue; the picker must not
      // OFFER it — the save would be refused (`ground_archived`). The
      // directory source below is already server-filtered (`GET /grounds`,
      // default excludes archived); these two personal/historical sources
      // carry the flag instead of dropping the rows, so the filter is ours.
      if (isArchived(v)) continue;
      put({
        name: v.ground,
        groundId: v.ground_id,
        city: v.city,
        country: v.country,
        placeType: v.place_type ?? null,
        source: "history",
      });
    }
    for (const g of search.rows) {
      put({
        name: g.name,
        groundId: g.ground_id,
        city: g.city,
        country: g.country,
        placeType: g.place_type ?? null,
        source: "directory",
      });
    }
    for (const g of savedGrounds) {
      if (isArchived(g)) continue;
      put({
        name: g.name,
        groundId: g.ground_id,
        city: g.city,
        country: g.country,
        placeType: g.place_type ?? null,
        source: "saved",
      });
    }
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [savedGrounds, venuesPlayed, search.rows]);

  /**
   * #356 zero-tap convergence. When the field holds a name with no record
   * behind it — a legacy row's stored string, or a value restored into a
   * re-opened form — resolve it against what has loaded, silently, ONCE.
   * `resolveStoredGround` refuses anything but an unambiguous whole-name
   * match, so this can attach a record but never guess between two.
   */
  useEffect(() => {
    if (groundId || !(value || "").trim() || suggestions.length === 0) return;
    if (!autoResolveWhileTyping && typedByPlayer.current) return;
    const hit = resolveStoredGround(value, suggestions);
    if (hit) onChange(hit.name, hit.groundId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, groundId, suggestions]);

  /**
   * Issue #689 — split off the caller's saved grounds into their own group,
   * BEFORE the general list is filtered and capped. See
   * `lib/groundSuggestions.js` for the precedence rule: a saved row that
   * matches the typed text is claimed here and never handed to the general
   * list below, so it cannot reappear lower down as if it had no
   * precedence.
   */
  const { saved: savedGroup, savedMatchCount, general: generalSuggestions } = useMemo(
    () => groupGroundSuggestions(suggestions, value),
    [suggestions, value],
  );

  // Issue #584: keep the pre-slice match count alongside the shown list so
  // the panel can say "showing 8 of N" instead of silently hiding rows behind
  // a scrollbar that fades out on macOS. Issue #689: this now runs over
  // `generalSuggestions` — the merged list MINUS whatever the saved group
  // above already claimed — so the two "Showing N of M" notes each describe
  // only the rows their own group is responsible for.
  //
  // Issue #690 — a `"directory"`-sourced row is NEVER re-filtered by name
  // here. It already matched the SERVER's search — on name, city OR country
  // (`groundsSearchWhere`) — and re-checking it against `s.name` alone would
  // silently drop a row that matched on city/country, which is exactly the
  // kind of narrower-than-intended answer #609's epic exists to prevent.
  // `"history"` rows are never searched server-side (#644 does not reach
  // match history — see `lib/directorySearch.js`'s `matchesSearchTerm`), so
  // they still need the client-side check.
  const { filtered, matchCount } = useMemo(() => {
    const q = (value || "").trim().toLowerCase();
    const matches = q
      ? generalSuggestions.filter((s) => s.source === "directory" || s.name.toLowerCase().includes(q))
      : generalSuggestions;
    return { filtered: matches.slice(0, 8), matchCount: matches.length };
  }, [value, generalSuggestions]);

  // Issue #210: "nothing typed yet" and "typed something we don't know" are
  // different states and must not share a rendering. Only the second one is
  // the add-first state — offering `Add ""` to someone who has merely
  // focused an empty field would be noise. Issue #689: a typed value that
  // matches nothing in the general list but DOES match a saved ground is not
  // "no match" — the saved group above answers it, so the add-first state
  // must not also render underneath it. Issue #690 — nor is a typed value
  // whose SEARCH has not answered yet: `search.pending` holds off the
  // add-first state for the same reason `useDirectorySearch`'s own header
  // gives (rule 5) — a question with no answer yet is not "no match", and
  // rendering it as one would flash "Add ..." for every keystroke before
  // quietly taking it back once the debounced answer lands.
  const typed = (value || "").trim();
  const noMatch = typed.length > 0 && filtered.length === 0 && savedGroup.length === 0 && !search.pending;

  /**
   * Requirement 2, upgraded by #356: a typed value that case-insensitively
   * names a ground the caller can see snaps to that RECORD — canonical
   * spelling and id together. A value that names nothing is left exactly as
   * typed and stays unresolved, which is what puts the form's own
   * "pick this ground" message on screen instead of a silent failure at
   * save time.
   */
  const snapToCanonical = () => {
    const trimmed = (value || "").trim();
    if (!trimmed) return;
    const hit = resolveStoredGround(trimmed, suggestions);
    if (hit && (hit.name !== value || hit.groundId !== groundId)) onChange(hit.name, hit.groundId);
  };

  const handleBlur = () => {
    // Deferred so a mousedown on a dropdown option (which calls
    // preventDefault to keep focus) or the "Add new ground…" button still
    // registers its click before the dropdown unmounts.
    blurTimer.current = setTimeout(() => {
      snapToCanonical();
      setOpen(false);
    }, 150);
  };

  // `tab` is the one-tap path the user picked ("search" | "manual"); the
  // headline "Add …" passes nothing and lets the dialog choose, which for a
  // seeded name means manual — the path that always succeeds with no
  // network round trip.
  const openAddDialog = (tab = null) => {
    setDialogSeed((value || "").trim());
    setDialogTab(tab);
    setOpen(false);
    setDialogOpen(true);
  };

  const handleSaved = (ground) => {
    // #356: the dialog returns a real record, so the field is resolved the
    // instant it closes — Add-first is now the fastest path to a VALID
    // field, not merely to a filled one.
    onChange(ground.name, ground.ground_id);
    setAddHerePin(null);
    loadSaved(); // new/linked ground is auto-bookmarked server-side — refresh so it's a suggestion immediately
    // Issue #690 — the general suggestion list is now the LIVE search
    // (`search.rows`), re-asked on every keystroke, so there is nothing of
    // ITS to invalidate here. `directoryRef` is near-me's own cache, and it
    // is the one copy left that can go stale: without clearing it, a ground
    // added just now would be missing from a near-me ranking until the
    // component remounted.
    directoryRef.current = null;
  };

  // ---------- Issue #134 ----------

  /**
   * The directory to rank, fetched on the FIRST near-me press and cached —
   * see the field's own comment above for why this is the one `GET /grounds`
   * left in this component. Issue #690 removed the mount-time fetch this
   * used to be able to reuse (`#356: the suggestion list already fetched
   * this on mount`); it now always does its own fetch, still before any
   * position is read.
   *
   * `GET /grounds` returns every global/verified ground plus the caller's
   * own, capped server-side at `GROUNDS_BBOX_MAX_ROWS`; merged with the
   * player's bookmarks (which may include a ground outside that scope) and
   * deduped by id. Neither request knows anything about the device — they
   * run BEFORE a position is read, which is the ordering that makes
   * "nothing carries the position" true by construction rather than by
   * inspection.
   *
   * Returns `null` when the directory can't be loaded, which is a different
   * answer from "nothing is near you" and gets its own line.
   */
  const loadDirectory = useCallback(async () => {
    if (directoryRef.current) return directoryRef.current;
    let visible;
    try {
      const r = await api.get("/grounds");
      // Issue #690 — the bare route now answers the same envelope shape the
      // bbox reads do (`{ mode, grounds, truncated, row_cap }`) rather than
      // a plain array; `readViewportGrounds` already reads either shape
      // (it is what every bbox-aware caller on this page uses), so this
      // degrades to the pre-#690 array reading exactly as before if the
      // server ever falls back to one.
      visible = readViewportGrounds(r?.data).grounds;
    } catch {
      return null; // don't cache a failure — the next press retries
    }
    const byId = new Map();
    for (const g of visible) byId.set(g.ground_id, g);
    for (const g of savedGrounds) if (g.ground_id && !byId.has(g.ground_id)) byId.set(g.ground_id, g);
    directoryRef.current = [...byId.values()];
    return directoryRef.current;
  }, [savedGrounds]);

  /**
   * Fires ONLY from the button's own click handler — never on mount, focus
   * or render. A permission prompt nobody asked for is the fastest way to
   * get location denied permanently.
   */
  const handleNearMe = async () => {
    setNearMe({ ...NEAR_ME_IDLE, status: "locating" });
    const directory = await loadDirectory();
    if (directory === null) {
      setNearMe({ ...NEAR_ME_IDLE, status: "error", message: DIRECTORY_UNAVAILABLE_MESSAGE });
      return;
    }
    const outcome = await findNearMe(directory);
    if (!outcome.ok) {
      setNearMe({ ...NEAR_ME_IDLE, status: "error", message: outcome.message });
      return;
    }
    setNearMe({ status: "ready", results: outcome.results, message: "", position: outcome.position });
  };

  /** Typing is the always-available path; it takes the panel back. */
  const resetNearMe = () => setNearMe((prev) => (prev.status === "idle" ? prev : NEAR_ME_IDLE));

  /**
   * Typing UNRESOLVES the field (#356). It has to: the id belongs to the
   * name it was picked with, and letting it survive an edit of the text
   * would let a player relabel a match away from the ground it points at —
   * which the API refuses anyway (`ground_required`), so keeping the pair
   * honest here is the difference between a clear form and a save-time 400.
   */
  const handleTyped = (next) => {
    /*
     * ...and typing REOPENS the panel (#166). Found by journey 23, which is
     * the first test to CHANGE a ground it had already picked: taking a
     * suggestion closes the dropdown while leaving the input focused (the
     * option's `onMouseDown` preventDefault is what keeps focus), so a second
     * click on the field fires no `focus` event and `onFocus` never runs. The
     * field then accepted keystrokes with no suggestions under them — the one
     * state #356 made unsavable — and the only way out was to click somewhere
     * else and back. Opening here rather than widening `onFocus` keeps the
     * rule "nothing opens until the user does something", which is why the
     * near-me prompt still cannot fire on mount.
     */
    setOpen(true);
    resetNearMe();
    typedByPlayer.current = true;
    onChange(next, null);
  };

  /**
   * The second explicit press. Only here does the position become anything
   * durable — a ground the player is choosing to add at that spot, through
   * the pre-existing `presetCoords` path (which reverse-geocodes city and
   * country, `GroundSearchDialog.jsx`). Ranking a list never does this.
   */
  const addHere = () => {
    if (!nearMe.position) return;
    // `fromDevice` rides on the pin so the dialog can tell "where the
    // player is" from "where the player clicked" — it is what stops the
    // duplicate check putting a live position in our own request logs
    // (`GroundSearchDialog.jsx`, #134's adversarial review).
    setAddHerePin({ lat: nearMe.position.lat, lng: nearMe.position.lng, fromDevice: true });
    setDialogSeed((value || "").trim());
    setDialogTab(null);
    setOpen(false);
    setDialogOpen(true);
  };

  return (
    <div className="relative">
      <Input
        data-testid={inputTestId}
        className={className}
        style={inputStyle}
        value={value}
        disabled={disabled}
        onChange={(e) => handleTyped(e.target.value)}
        onFocus={() => { if (!disabled && (openOnProgrammaticFocus || !isProgrammaticFocus())) setOpen(true); }}
        onClick={openOnProgrammaticFocus ? undefined : () => { if (!disabled) setOpen(true); }}
        onBlur={handleBlur}
        placeholder={placeholder}
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        aria-invalid={ariaInvalid}
        aria-describedby={ariaDescribedBy}
      />
      {open && !disabled && (
        <div
          className="absolute z-20 mt-1 w-full rounded-md border border-slate-200 bg-white shadow-lg max-h-60 overflow-y-auto overscroll-contain"
          // Issue #584: the container's own chrome — the scrollbar, the
          // padding, the gaps between rows — is not inside the input's focus,
          // so a mousedown on it (e.g. grabbing the scrollbar to drag) blurs
          // the input and collapses the panel mid-drag. Every clickable child
          // already carries this same guard; the container needs it too so
          // dragging the scrollbar keeps the panel open. preventDefault
          // suppresses only the focus change — child buttons' onClick still
          // fires, so selection behaviour is unchanged.
          onMouseDown={(e) => e.preventDefault()}
          style={dropdownOffset ? { marginTop: dropdownOffset } : undefined}
          data-testid={`${testIdPrefix}-dropdown`}
        >
          {/*
            Issue #134. Always offered, in both dropdown states, because
            "nearest to me" is as useful when nothing has been typed as when
            something unrecognised has. Nothing happens until it is pressed:
            no prompt on mount, no prompt on focus.

            The busy state is a word, not a spinner — this panel is the one
            surface in the flow that would otherwise animate, and a label
            that changes to "Finding you…" reads identically with reduced
            motion on.
          */}
          <div className="p-2 border-b border-slate-100">
            <button
              type="button"
              className="w-full text-left px-3 min-h-11 py-2 rounded-md flex items-center gap-2 min-w-0 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-60"
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleNearMe}
              disabled={nearMe.status === "locating"}
              data-testid={`${testIdPrefix}-near-me`}
            >
              <LocateFixed size={14} className="shrink-0" style={{ color: "#0B2E59" }} />
              <span className="truncate">{nearMe.status === "locating" ? NEAR_ME_BUSY_LABEL : NEAR_ME_LABEL}</span>
            </button>
            {/* One plain line, and the field above is untouched — denied,
                unavailable or timed out, typing still works. */}
            {nearMe.status === "error" && (
              <p
                className="px-3 pt-1 pb-0.5 text-[11px] text-slate-500"
                role="status"
                aria-live="polite"
                data-testid={`${testIdPrefix}-near-me-message`}
              >
                {nearMe.message}
              </p>
            )}
          </div>
          {nearMe.status === "ready" && (
            <div className="p-1" data-testid={`${testIdPrefix}-near-me-results`}>
              {nearMe.results.length > 0 ? (
                <>
                  <p className="px-3 pt-1 pb-1 text-[11px] uppercase tracking-wide text-slate-400">
                    Nearest to you
                  </p>
                  {nearMe.results.map((g) => (
                    <button
                      type="button"
                      key={g.ground_id || g.name}
                      className="w-full text-left px-3 min-h-11 py-2 rounded-md flex items-center justify-between gap-2 min-w-0 hover:bg-slate-50 text-sm"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => { onChange(g.name, g.ground_id); resetNearMe(); setOpen(false); }}
                      data-testid={`${testIdPrefix}-near-option-${g.name}`}
                    >
                      <span className="flex items-center gap-1.5 min-w-0">
                        <MapPin size={12} className="shrink-0 text-[#0B2E59]" />
                        <span className="truncate">{g.name}</span>
                        {/* Issue #136: which of the two nearby entries this
                            is — the near-me list is where it earns its keep. */}
                        <PlaceTypeTag value={g.place_type} testId={`${testIdPrefix}-near-type-${g.name}`} />
                      </span>
                      {/* The distance is what makes the suggestion trustworthy
                          (issue #133) — never render the ordering without it. */}
                      <span className="text-[11px] font-semibold text-slate-500 shrink-0 tabular-nums">
                        {g._distanceLabel}
                      </span>
                    </button>
                  ))}
                </>
              ) : (
                <div className="p-2 space-y-1" data-testid={`${testIdPrefix}-near-me-empty`}>
                  <p className="px-1 text-[11px] text-slate-500">
                    No known grounds within {NEAR_ME_RADIUS_KM} km of you.
                  </p>
                  {/*
                    Issue #134, adversarial review: this is where the two
                    consent events have to become distinguishable. Ranking
                    the list sent nothing anywhere. Adding a ground here
                    creates a public record at these coordinates AND asks
                    OpenStreetMap what town they are in — before the save,
                    on open. Saying so on the button that does it is the
                    difference between a choice and a surprise.
                  */}
                  <p className="px-1 pb-0.5 text-[11px] text-slate-500" data-testid={`${testIdPrefix}-near-me-add-note`}>
                    We&apos;ll look up the town and country for these coordinates.
                  </p>
                  <button
                    type="button"
                    className="w-full text-left px-3 min-h-11 py-2.5 rounded-md flex items-center gap-2 min-w-0 text-sm font-semibold text-white"
                    style={{ background: "#0B2E59" }}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={addHere}
                    data-testid={`${testIdPrefix}-near-me-add`}
                  >
                    <Plus size={15} className="shrink-0" />
                    <span className="truncate">Add this place</span>
                  </button>
                </div>
              )}
            </div>
          )}
          {/* The #210 no-match state and the ordinary suggestion list are
              unchanged — near-me results simply take their place while they
              are on screen, and typing takes the panel straight back. */}
          {nearMe.status !== "ready" && (
          <>
          {/*
            Issue #689. The player's own bookmarks, at the HEAD of the panel
            rather than sorted into the alphabetical merge below — the same
            treatment the "Nearest to you" group above gets (issue #134),
            minus the button, because nothing has to be pressed to know
            what you saved.
            Rendered whenever the field is empty (every saved ground matches)
            or the typed text matches at least one (`groupGroundSuggestions`).
            A player with no bookmarks — every coach, by design (#356
            decision 1), and any player who has never saved a ground — gets
            NO heading and no empty box: `savedGroup` is simply `[]`.
          */}
          {savedGroup.length > 0 && (
            <div className="p-1 border-b border-slate-100" data-testid={`${testIdPrefix}-saved-group`}>
              <p className="px-3 pt-1 pb-1 text-[11px] uppercase tracking-wide text-slate-400">
                Saved grounds
              </p>
              {savedGroup.map((s) => (
                <GroundOption
                  key={s.groundId || s.name}
                  ground={s}
                  onSelect={() => { onChange(s.name, s.groundId); setOpen(false); }}
                  testId={`${testIdPrefix}-saved-option-${s.name}`}
                />
              ))}
              {/* Cap ~5 (`SAVED_GROUP_CAP`), same "Showing N of M" pattern as
                  the general list's #584 note below. */}
              {savedMatchCount > savedGroup.length && (
                <p
                  className="px-3 pt-1.5 pb-1 text-[11px] text-slate-500"
                  data-testid={`${testIdPrefix}-saved-more-note`}
                >
                  Showing {savedGroup.length} of {savedMatchCount} — keep typing to narrow.
                </p>
              )}
            </div>
          )}
          {noMatch ? (
            <div className="p-2 space-y-1" data-testid={`${testIdPrefix}-no-match`}>
              {/* #356 landed: typing past this panel no longer saves. The
                  panel is now the ONLY way out of an unrecognised name, which
                  is why it leads with the action and why both add paths sit
                  directly under it. */}
              {/* The headline. Not a footnote, not preceded by an apology. */}
              <button
                type="button"
                className="w-full text-left px-3 min-h-11 py-2.5 rounded-md flex items-center gap-2 min-w-0 text-sm font-semibold text-white"
                style={{ background: "#0B2E59" }}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => openAddDialog()}
                data-testid={`${testIdPrefix}-add-new`}
              >
                <Plus size={15} className="shrink-0" />
                <span className="truncate">Add &quot;{typed}&quot;</span>
              </button>
              <p className="px-3 pt-1 pb-0.5 text-[11px] text-slate-500">
                Pins it on your map, and it&apos;s suggested next time.
              </p>
              <button
                type="button"
                className="w-full text-left px-3 min-h-11 py-2 rounded-md flex items-center gap-2 min-w-0 text-sm text-slate-700 hover:bg-slate-50"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => openAddDialog("search")}
                data-testid={`${testIdPrefix}-add-search`}
              >
                <Globe2 size={14} className="shrink-0" style={{ color: "#0B2E59" }} />
                <span className="truncate">Search worldwide</span>
              </button>
              <button
                type="button"
                className="w-full text-left px-3 min-h-11 py-2 rounded-md flex items-center gap-2 min-w-0 text-sm text-slate-700 hover:bg-slate-50"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => openAddDialog("manual")}
                data-testid={`${testIdPrefix}-add-manual`}
              >
                <MapPin size={14} className="shrink-0" style={{ color: "#0B2E59" }} />
                <span className="truncate">Enter manually</span>
              </button>
            </div>
          ) : (
            <>
              {filtered.map((s) => (
                <GroundOption
                  key={s.groundId || s.name}
                  ground={s}
                  onSelect={() => { onChange(s.name, s.groundId); setOpen(false); }}
                  testId={`${testIdPrefix}-option-${s.name}`}
                />
              ))}
              {/* Issue #584: the list is capped at 8. When more match, say so
                  — a scrollbar that fades out (macOS default) is not enough to
                  tell the user rows are hidden. */}
              {matchCount > filtered.length && (
                <p
                  className="px-3 pt-1.5 pb-1 text-[11px] text-slate-500"
                  data-testid={`${testIdPrefix}-more-note`}
                >
                  Showing {filtered.length} of {matchCount} — keep typing to narrow.
                </p>
              )}
              <button
                type="button"
                className="w-full text-left px-3 min-h-11 py-2 flex items-center gap-1.5 text-sm font-semibold border-t border-slate-100 hover:bg-slate-50"
                style={{ color: "#0B2E59" }}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => openAddDialog()}
                data-testid={`${testIdPrefix}-add-new`}
              >
                <Plus size={13} /> Add new ground…
              </button>
            </>
          )}
          </>
          )}
        </div>
      )}
      <GroundSearchDialog
        open={dialogOpen}
        onOpenChange={(o) => { setDialogOpen(o); if (!o) setAddHerePin(null); }}
        initialQuery={dialogSeed}
        initialTab={dialogTab}
        presetCoords={addHerePin}
        onSaved={handleSaved}
        hideIndoor={hideIndoor}
        markFieldErrors={markFieldErrors}
      />
    </div>
  );
}
